import React from 'react';

const formatNumber = (v) => {
  if (v === null || v === undefined || v === '') return '0';
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  if (Number.isNaN(n)) return '0';
  return n.toLocaleString('ko-KR');
};

const parseNumber = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isNaN(n) ? 0 : n;
};

const stripMarkup = (v) => {
  if (!v) return '';
  return String(v).replace(/<[^>]*>/g, '').trim();
};

const getReservationBadge = (code, name) => {
  const cleaned = stripMarkup(name);
  const colorMap = {
    '049001': 'bg-blue-100 text-blue-700',
    '049002': 'bg-amber-100 text-amber-700',
    '049011': 'bg-emerald-100 text-emerald-700',
    '049019': 'bg-indigo-100 text-indigo-700',
    '049021': 'bg-red-100 text-red-700',
    '049022': 'bg-red-100 text-red-700',
    '049029': 'bg-red-100 text-red-700',
  };
  const cls = colorMap[code] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {cleaned || '-'}
    </span>
  );
};

const GroupSettlementDetailModal = ({ open, onClose, group, onOpenReservationDetail, onOpenSettlement }) => {
  if (!open || !group) return null;

  const sales = Array.isArray(group.sales) ? group.sales : [];
  const hasSaleNo = sales.some((sale) => sale?.판매번호);
  const totals = sales.reduce(
    (acc, sale) => {
      const net = parseNumber(sale.판매현황NET);
      const dep = parseNumber(sale.판매현황입금액);
      const misu = net - dep;
      const commission = parseNumber(sale.대리점수수료);
      acc.net += net;
      acc.deposit += dep;
      acc.misu += misu;
      acc.commission += commission;
      return acc;
    },
    { net: 0, deposit: 0, misu: 0, commission: 0 }
  );

  const handleReservationClick = (sale) => {
    if (!onOpenReservationDetail) return;
    onOpenReservationDetail(sale);
  };

  const handleOpenSettlement = () => {
    if (!onOpenSettlement || !hasSaleNo) return;
    onOpenSettlement(group);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-3 gap-3">
          <div className="min-w-0">
            <div className="text-[17px] font-bold text-gray-900">단체 정산 상세</div>
            <div className="mt-1 text-xs text-gray-600">
              <span className="font-medium text-gray-800 mr-2">{group.행사단체명 || '-'}</span>
              <span className="text-gray-400">/</span>
              <span className="ml-2 text-gray-600">단체번호 {group.행사단체번호}</span>
              {group.시작일 && group.종료일 && (
                <span className="ml-3 text-gray-500 text-[11px]">
                  ({group.시작일} ~ {group.종료일})
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onOpenSettlement && (
              <button
                type="button"
                onClick={handleOpenSettlement}
                disabled={!hasSaleNo}
                title={hasSaleNo ? '정산서 열기' : '판매번호가 없어 정산서를 열 수 없습니다'}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 text-sm font-semibold rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                정산서 바로가기
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex size-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-xs bg-gray-50 rounded-xl p-4">
            <div>
              <span className="text-gray-500">정산기준일:</span>{' '}
              <span className="font-medium text-gray-800">{group.정산기준일 || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">수배담당자:</span>{' '}
              <span className="font-medium text-gray-800">{group.수배담당자 || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">수배담당부서:</span>{' '}
              <span className="font-medium text-gray-800">{group.수배담당부서 || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">취소건수:</span>{' '}
              <span className="font-medium text-gray-800">{formatNumber(group.취소건수)}</span>
            </div>
            <div>
              <span className="text-gray-500">취소인원:</span>{' '}
              <span className="font-medium text-gray-800">{group.취소인원}</span>
            </div>
            <div>
              <span className="text-gray-500">처리일시:</span>{' '}
              <span className="font-medium text-gray-800">{group.처리일시 || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">처리자:</span>{' '}
              <span className="font-medium text-gray-800">{group.처리자 || '-'}</span>
            </div>
          </div>

          <div className="rounded-xl bg-gray-50 overflow-hidden">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">판매 상세 ({sales.length})</div>
              <div className="text-[11px] text-gray-500">
                판매NET, 입금액, 미수액, 대리점수수료 합계를 하단에서 확인할 수 있습니다.
              </div>
            </div>
            <div className="overflow-x-auto max-h-[50vh]">
              <table className="min-w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">판매번호</th>
                    <th className="text-left h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">대리점</th>
                    <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">판매NET</th>
                    <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">입금액</th>
                    <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">미수액</th>
                    <th className="text-right h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">대리점수수료</th>
                    <th className="text-center h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">예약상태</th>
                    <th className="text-center h-10 bg-gray-100 px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">주문번호</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale, idx) => {
                    const saleNet = parseNumber(sale.판매현황NET);
                    const saleDeposit = parseNumber(sale.판매현황입금액);
                    const saleMisu = saleNet - saleDeposit;
                    return (
                      <tr
                        key={idx}
                        className={`${idx % 2 === 0 ? 'bg-white' : 'bg-white'} hover:bg-gray-50`}
                      >
                        <td className="px-3 py-3 border-b border-gray-200">
                          {sale.주문번호 ? (
                            <button
                              type="button"
                              className="text-blue-600 font-medium hover:text-blue-800 hover:underline"
                              onClick={() => handleReservationClick(sale)}
                              title="예약 상세 보기"
                            >
                              {sale.판매번호 || '-'}
                            </button>
                          ) : (
                            <span className="text-gray-600">{sale.판매번호 || '-'}</span>
                          )}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-200 text-gray-800">{sale.대리점 || '-'}</td>
                        <td className="px-3 py-3 border-b border-gray-200 text-right text-gray-800">
                          {formatNumber(sale.판매현황NET)}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-200 text-right text-gray-800">
                          {formatNumber(sale.판매현황입금액)}
                        </td>
                        <td
                          className={`px-3 py-3 border-b border-gray-200 text-right ${
                            saleMisu > 0 ? 'text-red-600 font-semibold' : 'text-gray-700'
                          }`}
                        >
                          {formatNumber(saleMisu)}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-200 text-right text-gray-700">
                          {formatNumber(sale.대리점수수료)}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-200 text-center">
                          {getReservationBadge(sale.예약상태코드, sale.예약상태명)}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-200 text-center text-gray-600">
                          {sale.주문번호 || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {sales.length > 0 && (
                  <tfoot className="bg-gray-100 font-semibold">
                    <tr>
                      <td className="px-3 py-3 border-t border-gray-300" colSpan={2}>
                        합계
                      </td>
                      <td className="px-3 py-3 border-t border-gray-300 text-right">
                        {formatNumber(totals.net)}
                      </td>
                      <td className="px-3 py-3 border-t border-gray-300 text-right">
                        {formatNumber(totals.deposit)}
                      </td>
                      <td
                        className={`px-3 py-3 border-t border-gray-300 text-right ${
                          totals.misu > 0 ? 'text-red-600' : 'text-gray-700'
                        }`}
                      >
                        {formatNumber(totals.misu)}
                      </td>
                      <td className="px-3 py-3 border-t border-gray-300 text-right">
                        {formatNumber(totals.commission)}
                      </td>
                      <td className="px-3 py-3 border-t border-gray-300" colSpan={2}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>

        <div className="px-6 pb-5 pt-3 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupSettlementDetailModal;
